import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { getCampaignBurnRate } from '../../../utils/creditPricing';

interface CampaignBurnRateProps {
  availableCredits: number;
  channelLabels: string[];
  outreachBlocked: boolean;
  weeklyLimit: number | null | undefined;
  totalWeeklyBurnAllCampaigns?: number | null;
  status?: string;
  /** Team has mod_sales active — without it, outreach cost is excluded */
  hasSalesModule?: boolean;
}

const getWeeksColor = (weeks: number | null) => {
  if (weeks === null) return 'text-gray-500';
  if (weeks > 8) return 'text-emerald-300';
  if (weeks >= 4) return 'text-amber-300';
  return 'text-red-300';
};

export const CampaignBurnRate: React.FC<CampaignBurnRateProps> = ({
  availableCredits,
  channelLabels,
  outreachBlocked,
  weeklyLimit,
  totalWeeklyBurnAllCampaigns,
  status,
  hasSalesModule = true,
}) => {
  // Without mod_sales, treat outreach as blocked (cost excluded)
  const effectiveOutreachBlocked = outreachBlocked || !hasSalesModule;

  const isActive = status === 'active' || !status;
  const burn = getCampaignBurnRate({
    channelLabels,
    outreachBlocked: effectiveOutreachBlocked,
    weeklyLimit,
    availableCredits,
  });

  const isArchived = status === 'archived';
  const weeksColor = getWeeksColor(burn.weeksRemaining);

  const combinedWeeksRemaining = totalWeeklyBurnAllCampaigns && totalWeeklyBurnAllCampaigns > 0
    ? Math.floor(availableCredits / totalWeeklyBurnAllCampaigns)
    : null;

  // ── Carousel items ────────────────────────────────────────────────
  interface CarouselItem {
    key: string;
    icon: string;
    iconColor: string;
    textColor: string;
    label: string;
    extra?: React.ReactNode;
  }

  const carouselItems: CarouselItem[] = [
    {
      key: 'cost',
      icon: 'solar:fire-bold-duotone',
      iconColor: 'text-amber-400/50',
      textColor: 'text-amber-400/70',
      label: `${burn.costPerLead} credits each lead`,
    },
    ...(burn.weeklyBurn !== null
      ? [{
          key: 'weekly' as const,
          icon: 'solar:chart-2-bold-duotone' as const,
          iconColor: 'text-indigo-300/50',
          textColor: 'text-indigo-300/70',
          label: `${burn.weeklyBurn.toLocaleString()} credits/week`,
        }]
      : [{
          key: 'weekly' as const,
          icon: 'solar:chart-2-bold-duotone' as const,
          iconColor: 'text-gray-500/50',
          textColor: 'text-gray-500',
          label: 'Set weekly limit to see spend',
        }]),
    ...(burn.weeksRemaining !== null
      ? [{
          key: 'weeks' as const,
          icon: 'solar:alarm-bold-duotone' as const,
          iconColor: `${weeksColor} opacity-50`,
          textColor: weeksColor,
          label: `Credits last ~${burn.weeksRemaining} week${burn.weeksRemaining !== 1 ? 's' : ''}`,
          extra: combinedWeeksRemaining !== null && combinedWeeksRemaining !== burn.weeksRemaining
            ? <span className="opacity-40">(all: ~{combinedWeeksRemaining}w)</span>
            : undefined,
        }]
      : [{
          key: 'weeks' as const,
          icon: 'solar:alarm-bold-duotone' as const,
          iconColor: 'text-gray-500/50',
          textColor: 'text-gray-500',
          label: '--',
        }]),
  ];

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const intervalRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      setFade(false);
      window.setTimeout(() => {
        setCarouselIndex((prev) => (prev + 1) % carouselItems.length);
        setFade(true);
      }, 400);
    }, 2800);
    return () => window.clearInterval(intervalRef.current);
  }, [carouselItems.length]);

  const current = carouselItems[carouselIndex];

  // Not active — show cost per lead but no weekly burn / weeks remaining
  if (!isActive) {
    return (
      <div className={`mt-3 pt-3 pl-1 ${isArchived ? 'opacity-50' : 'opacity-40'}`}>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E] shrink-0">
            Burn Rate
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.1em] text-amber-400/50">
            <Icon icon="solar:fire-bold-duotone" className="h-3 w-3 text-amber-400/40" />
            {burn.costPerLead} credits each lead
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.1em] text-gray-500">
            {status === 'paused' ? 'Paused — not consuming credits' : 'Archived — no credit spend'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-3 pt-3 pl-1 ${isArchived ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Section Title */}
        <span className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E] shrink-0">
          Burn Rate
        </span>

        {/* Carousel Stat — cycles cost/lead → weekly spend → weeks remaining */}
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.1em] transition-all duration-400 ${current.textColor} ${
            fade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-0.5'
          }`}
        >
          <Icon icon={current.icon} className={`h-3 w-3 ${current.iconColor}`} />
          {current.label}
          {current.extra}
        </span>
      </div>
    </div>
  );
};

export default CampaignBurnRate;